import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { INSTAGRAM_URL, INSTAGRAM_HANDLE, INSTAGRAM_FEED_URL } from '../../config';
import useInView from '../../hooks/useInView';
import styles from './InstagramFeed.module.css';

const TILE_COUNT = 6;

/** Local night photos shown until the live feed is connected (or if it fails). */
const FALLBACK_TILES = Array.from({ length: TILE_COUNT }, (_, i) => ({
  id: `local-${i + 1}`,
  permalink: INSTAGRAM_URL,
  src: `/images/strip-${i + 1}.jpg`,
  alt: 'Worship at a Revive Night',
  caption: '',
  isVideo: false,
}));

/** Map a Behold post object to the tile shape, defensively. */
function mapPost(post) {
  const src =
    post.sizes?.medium?.mediaUrl ||
    post.sizes?.small?.mediaUrl ||
    post.thumbnailUrl ||
    post.mediaUrl;
  if (!src) return null;
  return {
    id: post.id || post.permalink,
    permalink: post.permalink || INSTAGRAM_URL,
    src,
    alt: post.prunedCaption || post.caption
      ? `Instagram post: ${(post.prunedCaption || post.caption).slice(0, 80)}`
      : 'Revive Worship on Instagram',
    caption: post.prunedCaption || post.caption || '',
    isVideo: post.mediaType === 'VIDEO',
  };
}

/**
 * Live Instagram grid — fetches the Behold JSON feed once the section
 * scrolls near the viewport. Falls back to our own photo strip (linking
 * to the profile) when no feed is configured or the fetch fails, so the
 * section is never broken and never empty.
 */
export default function InstagramFeed() {
  const [ref, inView] = useInView({ threshold: 0.05 });
  const [posts, setPosts] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!inView || posts || failed || !INSTAGRAM_FEED_URL) return;
    let cancelled = false;
    fetch(INSTAGRAM_FEED_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('feed error'))))
      .then((data) => {
        if (cancelled) return;
        const list = (data.posts || data.media || [])
          .map(mapPost)
          .filter(Boolean)
          .slice(0, TILE_COUNT);
        if (list.length) setPosts(list);
        else setFailed(true);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [inView, posts, failed]);

  const live = Boolean(posts);
  const tiles = useMemo(() => posts || FALLBACK_TILES, [posts]);

  return (
    <section id="journey" className={styles.section} ref={ref}>
      <div className={styles.shell}>
        <header className={styles.head}>
          <div className={styles.headText}>
            <span className={styles.eyebrow}>
              {live && <span className={styles.liveDot} aria-hidden="true" />}
              {live ? 'Live from Instagram' : 'From our nights'}
            </span>
            <h2 className={styles.title}>
              The <em>journey</em>, as it happens.
            </h2>
            <p className={styles.scriptNote} aria-hidden="true">come see for yourself</p>
          </div>
          <a
            className={styles.followBtn}
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
          >
            {INSTAGRAM_HANDLE} <span aria-hidden="true">↗</span>
          </a>
        </header>

        <ul className={styles.grid}>
          {tiles.map((tile, i) => (
            <motion.li
              key={tile.id}
              className={styles.cell}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
            >
              <a
                className={styles.tile}
                href={tile.permalink}
                target="_blank"
                rel="noreferrer"
                aria-label={live ? 'Open this post on Instagram' : 'Open Revive Worship on Instagram'}
              >
                <img
                  src={tile.src}
                  alt={tile.alt}
                  loading="lazy"
                  decoding="async"
                />
                <span className={styles.tileVeil} aria-hidden="true">
                  {tile.isVideo && <span className={styles.tilePlay}>▶</span>}
                  <span className={styles.tileArrow}>↗</span>
                  {tile.caption && (
                    <span className={styles.tileCaption}>{tile.caption}</span>
                  )}
                </span>
              </a>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
